import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue'
import moment from 'moment'
import CompanyGroup from '@/components/weekly-schedule/CompanyGroup.vue'
import DragScrollHelper from '@/components/common/DragScrollHelper.vue'
import ResourceTypeGroup from '@/components/weekly-schedule/ResourceTypeGroup.vue'
import UnitGroup from '@/components/schedule/UnitGroup.vue'
import EventCell from '@/components/weekly-schedule/EventCell'
import Week from '@/components/weekly-schedule/Week.vue'
import { UnitNumber } from '@/models/unit_number'
import ResourceType from '@/models/resource_type'
import { groupBy, orderBy } from 'lodash'
import {
  constructResourceTypeOverview,
  getWeekOverviewForJobs,
  groupJobs,
  Item,
  Row,
  RowType,
  WeekDays,
} from '@/components/weekly-schedule/utils'
import { useStore, useGetters } from 'vuex-composition-helpers'
import { BusinessUnit } from '@/models/business-unit'
import Division from '../../models/division'
import CurrentUserContext from '@/models/current-user-context'

interface Props {
  resourceType: ResourceType
  jobs: Item[]
  weekDays: WeekDays
  filters: {
    selectedDivisionIds: (string | number)[]
    selectedCompanyIds: (string | number)[]
    showOfflineUnits: boolean
    showStandbyUnits: boolean
    showUnitsWithNoAssignedJobs: boolean
  }
}

export default /*#__PURE__*/_defineComponent({
  __name: 'ResourceTypeSchedule',
  props: {
    resourceType: null,
    jobs: null,
    weekDays: null,
    filters: null
  },
  emits: ['drop:day'],
  setup(__props: any, { emit }) {

const props = __props as {
  resourceType: ResourceType
  jobs: Item[]
  weekDays: WeekDays
  filters: {
    selectedDivisionIds: (string | number)[]
    selectedCompanyIds: (string | number)[]
    showOfflineUnits: boolean
    showStandbyUnits: boolean
    showUnitsWithNoAssignedJobs: boolean
  }
}

const store = useStore()



const { getById: getDivisionById } = useGetters('divisions', ['getById'])

const businessUnits = computed<BusinessUnit[]>(
  () => store.state.businessUnits.all
)
const divisions = computed<Record<number, Division>>(
  () => store.state.divisions.all
)
const resources = computed<Record<string, UnitNumber>>(
  () => store.state.unitNumbers.all
)
const userContext = computed<CurrentUserContext>(
  () => store.state.permissions.context
)

const unassignedJobs = computed(() =>
  props.jobs.filter(
    (j) =>
      j.status === 'Requested' &&
      userContext.value.lineage.some((l) => l.id === j.ownerId)
  )
)

const lookup = computed(() => {
  const assignedJobs = props.jobs.filter((j) => j.status !== 'Requested')
  const lookup: Record<`unit-${number}` | `company-${number}`, Item[]> = {}
  for (const job of assignedJobs) {
    if (job.unitId) {
      if (!(`unit-${job.unitId}` in lookup)) {
        lookup[`unit-${job.unitId}`] = []
      }
      lookup[`unit-${job.unitId}`].push(job)
    } else {
      if (!(`company-${job.serviceProviderBusinessUnitId}` in lookup)) {
        lookup[`company-${job.serviceProviderBusinessUnitId}`] = []
      }
      lookup[`company-${job.serviceProviderBusinessUnitId}`].push(job)
    }
  }
  return lookup
})

const resourcesForSelectedResourceType = computed(() =>
  orderBy(
    Object.values(resources.value).filter(
      (r) => r.isVisible && r.resourceTypeId === props.resourceType.id
    ),
    [
      (unit: UnitNumber) =>
        unit.organizationId
          ? businessUnits.value[unit.organizationId]?.name?.toLowerCase()
          : '',
      'isOffline',
      'isActive',
      'isStandby',
      (unit: UnitNumber) => unit.name?.toLowerCase(),
      (unit: UnitNumber) =>
        unit.divisionId ? divisions.value[unit.divisionId]?.name : null,
    ]
  )
)

const filteredResources = computed(() => {
  const visibleUnits = resourcesForSelectedResourceType.value
  return visibleUnits.filter(
    (unit: UnitNumber) =>
      (props.filters.showOfflineUnits || !unit.isOffline) &&
      (props.filters.showStandbyUnits || !unit.isStandby) &&
      (props.filters.showUnitsWithNoAssignedJobs ||
        props.jobs.some((j) => j.unitId === unit.id)) &&
      (props.filters.selectedDivisionIds.some(
        (id) => id === getDivisionById.value(unit.divisionId)?.uuid
      ) ||
        (unit.divisionId === null &&
          store.getters['permissions/getRoleAssignmentBusinessUnitIds'].some(
            (b) => b === unit.ownerId
          ))) &&
      props.filters.selectedCompanyIds.some((id) => id === unit.organizationId)
  )
})

const resourceTypeOverview = computed(() =>
  constructResourceTypeOverview(
    filteredResources.value,
    props.filters.showStandbyUnits,
    props.filters.showOfflineUnits,
    resourcesForSelectedResourceType.value.length
  )
)

const isTwoTier = computed(() => {
  const partners = new Set(
    userContext.value.lineage.flatMap((l) => l.partnerIds)
  )
  return partners.size > 0
})

const resourceTypeRow = computed<Row | null>(() => {
  if (isTwoTier.value) {
    return {
      id: props.resourceType.id ?? -1,
      type: RowType.ResourceType,
      item: props.resourceType,
      jobs: groupJobs(
        unassignedJobs.value.filter(
          (j) =>
            props.filters?.selectedDivisionIds.includes(j.division.id) ||
            props.filters?.selectedDivisionIds.includes(
              getDivisionById.value(j.division.id)?.uuid
            )
        )
      ),
      overview: getWeekOverviewForJobs(
        unassignedJobs.value,
        props.weekDays[0].date,
        'unassigned'
      ),
    }
  }

  const [serviceProvider] = userContext.value.lineage
    .filter((l) => l.type === 'Organization')
    .map((l) => businessUnits.value[l.id])
  const jobs = props.jobs.filter(
    (j) =>
      j.unitId === null &&
      userContext.value.lineage.some(
        (l) => l.id === j.serviceProviderBusinessUnitId
      ) &&
      !j.isCancelled
  )

  if (!serviceProvider) {
    return null
  }

  return {
    id: serviceProvider.id,
    type: RowType.ServiceProvider,
    item: serviceProvider,
    jobs: groupJobs(jobs),
    overview: getWeekOverviewForJobs(jobs, props.weekDays[0].date),
  }
})

const rows = computed(() => {
  const bySp = groupBy(filteredResources.value, 'organizationId')
  return Object.entries(bySp).flatMap(([organizationId, unitNumbers]) => {
    const rows: Row[] = []
    if (isTwoTier.value) {
      const serviceProviderJobs = lookup.value[
        `company-${organizationId}`
      ]?.filter((j) => !j.isCancelled)

      rows.push({
        id: organizationId,
        type: RowType.ServiceProvider,
        item: businessUnits.value[organizationId],
        jobs: groupJobs(serviceProviderJobs),
        overview: getWeekOverviewForJobs(
          lookup.value[`company-${organizationId}`],
          props.weekDays[0].date
        ),
      })
    }
    rows.push(
      ...[
        ...unitNumbers.map((un: UnitNumber) => ({
          id: un.id || -1,
          type: RowType.Resource,
          item: un,
          jobs: groupJobs(lookup.value[`unit-${un.id}`]),
          overview: getWeekOverviewForJobs(
            lookup.value[`unit-${un.id}`],
            props.weekDays[0].date
          ),
        })),
      ]
    )
    return rows
  })
})

const resourceTypeName = computed(() => {
  return props.resourceType.name ?? ''
})

function isServiceProvider(type: RowType) {
  return type === RowType.ServiceProvider
}

function isResource(type: RowType) {
  return type === RowType.Resource
}

function onDrop(date: moment.Moment, row: Row | null) {
  emit('drop:day', { date, type: row?.type, entity: row?.item })
}

return { __sfc: true,store, emit, props, getDivisionById, businessUnits, divisions, resources, userContext, unassignedJobs, lookup, resourcesForSelectedResourceType, filteredResources, resourceTypeOverview, isTwoTier, resourceTypeRow, rows, resourceTypeName, isServiceProvider, isResource, onDrop, CompanyGroup, DragScrollHelper, ResourceTypeGroup, UnitGroup, EventCell, Week }
}

})